import React, { useRef } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import PdfIcon from '@/icons/files/PdfIcon2';
import TxtIcon from '@/icons/files/TxtIcon';
import DocIcon from '@/icons/files/DocIcon';
import DocxIcon from '@/icons/files/DocxIcon';
import WebpIcon from '@/icons/files/WebpIcon';
import PngIcon from '@/icons/files/PngIcon';
import JpgIcon from '@/icons/files/JpgIcon';
import JpegIcon from '@/icons/files/JpegIcon';
import { getFileExtension } from '../../../utils/helpers/helpers';

const fileIcons: Record<string, () => React.ReactElement> = {
  pdf: PdfIcon,
  txt: TxtIcon,
  doc: DocIcon,
  docx: DocxIcon,
  jpg: JpgIcon,
  jpeg: JpegIcon,
  png: PngIcon,
  webp: WebpIcon,
};

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

interface AttachButtonProps {
  /** Called with newly-picked files; parent merges into its staged list. */
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * Paperclip icon button for comment composers. Styled to match the
 * bold/italic/underline toolbar buttons so it sits in the same group.
 */
export function CommentAttachButton({ onFiles, disabled }: AttachButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onFiles(files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        title="Attach files"
        className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors disabled:opacity-50"
      >
        <Icon icon="lucide:paperclip" width={16} height={16} />
      </button>
    </>
  );
}

interface StagedFileListProps {
  files: File[];
  onRemove: (index: number) => void;
  /** Whether uploads are in-flight (after the parent comment is created). */
  uploading?: boolean;
}

/**
 * Chip strip showing files staged for upload. Renders inside the composer
 * body (between the editor and the toolbar), not in the toolbar itself.
 */
export function StagedFileList({
  files,
  onRemove,
  uploading,
}: StagedFileListProps) {
  if (files.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
      {files.map((file, idx) => {
        const ext = getFileExtension(file.name);
        const IconComponent = fileIcons[ext];
        return (
          <div
            key={`${file.name}-${idx}`}
            className="flex items-center gap-2 px-2 py-1.5 bg-[#141414] rounded-md min-w-0 max-w-full"
          >
            {IconComponent && (
              <div className="flex-shrink-0">
                <IconComponent />
              </div>
            )}
            <div className="flex flex-col min-w-0 max-w-[180px]">
              <span
                className="text-xs text-text-primary-body truncate"
                title={file.name}
              >
                {file.name}
              </span>
              <span className="text-[10px] text-text-primary-body/70">
                {uploading ? 'Uploading…' : formatFileSize(file.size)}
              </span>
            </div>
            {!uploading && (
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="p-0.5 hover:bg-red-500/10 rounded-full"
              >
                <Icon
                  icon={'qlementine-icons:close-16'}
                  className="w-3 h-3 text-text-primary-body hover:text-error-2"
                />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
